import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

// Import translations
import en from './locales/en.json';
import es from './locales/es.json';
import fr from './locales/fr.json';
import de from './locales/de.json';
import ja from './locales/ja.json';
import ko from './locales/ko.json';
import zh from './locales/zh.json';
import nl from './locales/nl.json';
import pt from './locales/pt.json';
import it from './locales/it.json';
import ar from './locales/ar.json';

export const supportedLanguages = [
  { code: 'en', name: 'English', nativeName: 'English', flag: '🇬🇧', rtl: false },
  { code: 'es', name: 'Spanish', nativeName: 'Español', flag: '🇪🇸', rtl: false },
  { code: 'fr', name: 'French', nativeName: 'Français', flag: '🇫🇷', rtl: false },
  { code: 'de', name: 'German', nativeName: 'Deutsch', flag: '🇩🇪', rtl: false },
  { code: 'nl', name: 'Dutch', nativeName: 'Nederlands', flag: '🇳🇱', rtl: false },
  { code: 'pt', name: 'Portuguese', nativeName: 'Português', flag: '🇧🇷', rtl: false },
  { code: 'it', name: 'Italian', nativeName: 'Italiano', flag: '🇮🇹', rtl: false },
  { code: 'ja', name: 'Japanese', nativeName: '日本語', flag: '🇯🇵', rtl: false },
  { code: 'ko', name: 'Korean', nativeName: '한국어', flag: '🇰🇷', rtl: false },
  { code: 'zh', name: 'Chinese', nativeName: '中文', flag: '🇨🇳', rtl: false },
  { code: 'ar', name: 'Arabic', nativeName: 'العربية', flag: '🇸🇦', rtl: true },
] as const;

export const rtlLanguages = ['ar'] as const;

export type LanguageCode = typeof supportedLanguages[number]['code'];

export const defaultLanguage: LanguageCode = 'en';

// Extract language code from URL path
export const getLanguageFromPath = (pathname: string): LanguageCode => {
  const match = pathname.match(/^\/([a-z]{2})(?:\/|$)/);
  if (match) {
    const lang = match[1] as LanguageCode;
    if (supportedLanguages.some(l => l.code === lang) && lang !== 'en') {
      return lang;
    }
  }
  return 'en';
};

// Remove language prefix from path
export const getPathWithoutLanguage = (pathname: string): string => {
  const match = pathname.match(/^\/([a-z]{2})(?:\/(.*))?$/);
  if (match && supportedLanguages.some(l => l.code === match[1]) && match[1] !== 'en') {
    return '/' + (match[2] || '');
  }
  return pathname;
};

// Add language prefix to path
export const getLocalizedPath = (pathname: string, lang: LanguageCode): string => {
  const cleanPath = getPathWithoutLanguage(pathname);
  if (lang === 'en') {
    return cleanPath;
  }
  return `/${lang}${cleanPath === '/' ? '' : cleanPath}`;
};

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
  resources: {
      en: { translation: en },
      es: { translation: es },
      fr: { translation: fr },
      de: { translation: de },
      nl: { translation: nl },
      pt: { translation: pt },
      it: { translation: it },
      ja: { translation: ja },
      ko: { translation: ko },
      zh: { translation: zh },
      ar: { translation: ar },
    },
    fallbackLng: 'en',
    detection: {
      order: ['path', 'navigator'],
      lookupFromPathIndex: 0,
    },
    interpolation: {
      escapeValue: false,
    },
  });

export default i18n;
